// @spec IAM-STR-031
// Zustand store for impersonation state with sessionStorage persistence

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { ImpersonationTargetUser } from '../types';

import { createSelectors } from '@/stores/create-selectors';

interface ImpersonationState {
  token: string | null;
  targetUser: ImpersonationTargetUser | null;
  reason: string | null;
  startedAt: string | null;
  expiresAt: string | null;

  // Actions
  setImpersonation: (data: {
    token: string;
    targetUser: ImpersonationTargetUser;
    reason: string;
    expiresAt: string;
  }) => void;
  clearImpersonation: () => void;
  isExpired: () => boolean;
}

const useImpersonationStoreBase = create<ImpersonationState>()(
  persist(
    (set, get) => ({
      token: null,
      targetUser: null,
      reason: null,
      startedAt: null,
      expiresAt: null,

      setImpersonation: (data) => {
        set({
          token: data.token,
          targetUser: data.targetUser,
          reason: data.reason,
          startedAt: new Date().toISOString(),
          expiresAt: data.expiresAt,
        });
      },

      clearImpersonation: () => {
        set({
          token: null,
          targetUser: null,
          reason: null,
          startedAt: null,
          expiresAt: null,
        });
      },

      isExpired: () => {
        const { expiresAt } = get();
        if (!expiresAt) return true;
        return new Date(expiresAt) < new Date();
      },
    }),
    {
      name: 'quantifier-impersonation',
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) return null;
          return JSON.parse(str) as { state: ImpersonationState };
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    }
  )
);

/**
 * Impersonation store for managing Super Admin impersonation state.
 *
 * Uses sessionStorage persistence so impersonation ends when browser closes.
 *
 * @example
 * // Using auto-selectors (recommended)
 * const token = useImpersonationStore.use.token();
 * const targetUser = useImpersonationStore.use.targetUser();
 *
 * // Using actions
 * const setImpersonation = useImpersonationStore.use.setImpersonation();
 * const clearImpersonation = useImpersonationStore.use.clearImpersonation();
 */
export const useImpersonationStore = createSelectors(useImpersonationStoreBase);
